import { useCallback, useMemo, useSyncExternalStore } from 'react'

const STORAGE_KEY = 'parked-items'

interface SerializedParkedItems {
  readonly hosts: readonly string[]
  readonly projects: readonly string[]
  readonly sessions: readonly string[]
}

export interface ParkedItems {
  readonly hosts: ReadonlySet<string>
  readonly projects: ReadonlySet<string>
  readonly sessions: ReadonlySet<string>
}

export interface UseParkedItemsReturn {
  readonly parkedItems: ParkedItems
  readonly parkHost: (hostname: string) => void
  readonly parkProject: (projectKey: string) => void
  readonly parkSession: (sessionId: string) => void
  readonly unparkHost: (hostname: string) => void
  readonly unparkProject: (projectKey: string) => void
  readonly unparkSession: (sessionId: string) => void
  readonly isParked: (type: 'host' | 'project' | 'session', key: string) => boolean
}

const EMPTY_PARKED: ParkedItems = {
  hosts: new Set<string>(),
  projects: new Set<string>(),
  sessions: new Set<string>(),
}

function readFromStorage(): ParkedItems {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return EMPTY_PARKED

    const parsed: SerializedParkedItems = JSON.parse(raw)
    return {
      hosts: new Set(parsed.hosts),
      projects: new Set(parsed.projects),
      sessions: new Set(parsed.sessions),
    }
  } catch {
    return EMPTY_PARKED
  }
}

function writeToStorage(items: ParkedItems): void {
  try {
    const serialized: SerializedParkedItems = {
      hosts: [...items.hosts],
      projects: [...items.projects],
      sessions: [...items.sessions],
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(serialized))
  } catch {
    /* storage full or unavailable */
  }
}

let currentSnapshot = readFromStorage()
const listeners = new Set<() => void>()

function getSnapshot(): ParkedItems {
  return currentSnapshot
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function updateState(next: ParkedItems): void {
  currentSnapshot = next
  writeToStorage(next)
  for (const listener of listeners) {
    listener()
  }
}

function addToSet(set: ReadonlySet<string>, value: string): ReadonlySet<string> {
  if (set.has(value)) return set
  const next = new Set(set)
  next.add(value)
  return next
}

function removeFromSet(set: ReadonlySet<string>, value: string): ReadonlySet<string> {
  if (!set.has(value)) return set
  const next = new Set(set)
  next.delete(value)
  return next
}

export function useParkedItems(): UseParkedItemsReturn {
  const parkedItems = useSyncExternalStore(subscribe, getSnapshot, () => EMPTY_PARKED)

  const parkHost = useCallback((hostname: string) => {
    const current = getSnapshot()
    updateState({ ...current, hosts: addToSet(current.hosts, hostname) })
  }, [])

  const parkProject = useCallback((projectKey: string) => {
    const current = getSnapshot()
    updateState({ ...current, projects: addToSet(current.projects, projectKey) })
  }, [])

  const parkSession = useCallback((sessionId: string) => {
    const current = getSnapshot()
    updateState({ ...current, sessions: addToSet(current.sessions, sessionId) })
  }, [])

  const unparkHost = useCallback((hostname: string) => {
    const current = getSnapshot()
    updateState({ ...current, hosts: removeFromSet(current.hosts, hostname) })
  }, [])

  const unparkProject = useCallback((projectKey: string) => {
    const current = getSnapshot()
    updateState({ ...current, projects: removeFromSet(current.projects, projectKey) })
  }, [])

  const unparkSession = useCallback((sessionId: string) => {
    const current = getSnapshot()
    updateState({ ...current, sessions: removeFromSet(current.sessions, sessionId) })
  }, [])

  const isParked = useCallback(
    (type: 'host' | 'project' | 'session', key: string): boolean => {
      switch (type) {
        case 'host':
          return parkedItems.hosts.has(key)
        case 'project':
          return parkedItems.projects.has(key)
        case 'session':
          return parkedItems.sessions.has(key)
      }
    },
    [parkedItems],
  )

  return useMemo(
    () => ({
      parkedItems,
      parkHost,
      parkProject,
      parkSession,
      unparkHost,
      unparkProject,
      unparkSession,
      isParked,
    }),
    [
      parkedItems,
      parkHost,
      parkProject,
      parkSession,
      unparkHost,
      unparkProject,
      unparkSession,
      isParked,
    ],
  )
}
